<script lang="ts" setup>
import type { Section } from '~/types'

interface Props extends Pick<Section, 'collection' | 'id' | 'title' | 'description'> {}
const props = defineProps<Props>()

const page = ref(1)
const quantityOnPage = ref(3)

const col = computed<CollectionConfig>(() => GetCollection(props.collection))

const { data, refresh } = useAsyncData(`collectionStandout-${props.id}--${props.collection}`, async () => {
  const pb = useNuxtApp().$pb
  const collection = col.value?.pub?.collection ?? col.value?.collection ?? props.collection
  return await pb.collection(collection).getList(page.value, quantityOnPage.value, { sort: col.value?.pub?.defaultSortField || 'id' }).catch(() => EMPTY_LIST_RESULT)
})

const canLoadMore = computed(() => (data.value?.totalItems ?? 0) > (page.value * quantityOnPage.value))

function loadMore() {
  if (canLoadMore.value) {
    page.value++
    refresh()
  }
}

const items = computed(() => {
  const arr: any[] = [{ title: props.title }]
  if (data.value) {
    arr.push(...data.value.items)
  }
  if (canLoadMore.value) {
    arr.push({ title: col.value.title })
  }
  return arr
})
</script>

<template>
  <div class="mx-auto max-w-screen-2xl">
    <div class="bg-lightblue rounded-3xl p-6 md:p-12 lg:p-16 interblock relative z-[20]">
      <ClientOnly>
        <template v-if="items && col && col.collection !== 'products'">
          <masonry-wall
            :key="items.length"
            :items="items"
            :ssr-columns="1"
            :column-width="[450, 400, 300]"
            :gap="16"
            :min-columns="1"
            :max-columns="4"
          >
            <template #default="{ item, index }">
              <div
                v-if="index === 0"
                class="flex items-center justify-center py-12 flex-col gap-2"
              >
                <Icon
                  name="gbf:arrow-swish-right"
                  class="w-16 h-16 text-yellow  right-3 ml-auto mr-3"
                />
                <HeadingText
                  tag="h3"
                  class="text-white mx-auto"
                >
                  {{ title }}
                </HeadingText>
              </div>
              <div
                v-else-if="(index + 1) === items.length && canLoadMore"
                class="col-span-full lg:col-span-1 flex items-center justify-center"
              >
                <UButton
                  size="xl"
                  color="yellow"
                  @click="loadMore()"
                >
                  More {{ col.title }}
                </UButton>
              </div>

              <component
                :is="col.pub?.cardComponent"
                v-else
                :data="item"
              />
            </template>
          </masonry-wall>
        </template>
        <div
          v-else
          class="flex flex-wrap gap-6"
        >
          <div
            class="flex justify-start py-12 flex-col gap-2 w-full md:w-1/3"
          >
            <Icon
              name="gbf:arrow-swish-right"
              class="w-16 h-16 text-yellow  right-3 ml-auto mr-3"
            />
            <HeadingText
              tag="h3"
              class="text-white mx-auto"
            >
              {{ title }}
            </HeadingText>
          </div>
          <div class="grid flex-1 gap-3 lg:grid-cols-2 w-full lg:grid-rows-2">
            <component
              :is="col.pub?.cardComponent"
              v-for="(item, index) in items.slice(1, items.length +1)"
              :key="item.id"
              class=""
              :class="[index === 0 ? 'w-full row-span-2' : '']"
              :data="item"
            />
          </div>
        </div>
      </ClientOnly>
    </div>
  </div>
</template>

<style scoped>
.auto-rows-min {
  grid-auto-rows: min-content;
}
</style>
